import { ChevronLeftIcon, Save, X, Variable, List, ListOrdered, RemoveFormatting } from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import { useNavigate, useLocation } from "react-router-dom";
import { CustomTextEditor, Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { cn } from "../../utils/tailwindUtils";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { LETTER_TEMPLATE_API } from "../../constants/api";

const LetterTemplateAddEdit = () => {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        id: "",
        code: "",
        name: "",
        description: "",
        content: null,
        is_active: true,
        company_id: ""
    });

    const [searchTerm, setSearchTerm] = useState("");
    const editorRef = useRef(null);

    const { create, update } = useApiMethods();
    const navigate = useNavigate();
    const location = useLocation();
    const { fromEdit, editDetails, isActive } = location.state || {};
    const companyid = sessionStorage.getItem("customConfig") ? JSON.parse(sessionStorage.getItem("customConfig")).company_id : '';

    // Letter template variables
    const letterVariables = useMemo(() => [
        { key: "employee_name", label: "Employee Name" },
        { key: "designation", label: "Designation" },
        { key: "joining_date", label: "Joining Date" },
        { key: "salary", label: "Salary" },
        { key: "company_name", label: "Company Name" },
        { key: "department", label: "Department" },
        { key: "employee_id", label: "Employee ID" },
        { key: "current_date", label: "Current Date" },
    ], []);

    // Initialize form data
    useEffect(() => {
        if (fromEdit && editDetails) {
            setFormData({
                id: editDetails.id || "",
                code: editDetails.code || "",
                name: editDetails.name || "",
                description: editDetails.description || "",
                content: editDetails.content || "",
                is_active: editDetails.is_active ?? true,
                company_id: editDetails.company_id || companyid
            });
        } else {
            setFormData(prev => ({
                ...prev,
                company_id: companyid,
                content: "<p></p>" // Clean empty content
            }));
        }
    }, [fromEdit, editDetails, companyid]);

    const handleBack = () => {
        navigate('/master/lettertemplate');
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Validation function
    const validateForm = () => {
        if (!formData.name?.trim()) {
            ToastMsg("error", "Template Name is required!");
            return false;
        }

        if (!formData.description?.trim()) {
            ToastMsg("error", "Template Description is required!");
            return false;
        }

        // Check if content is empty
        const cleanContent = formData.content?.replace(/<[^>]*>/g, '').trim();
        if (!cleanContent) {
            ToastMsg("error", "Content is required!");
            return false;
        }

        return true;
    };

    const handleSave = async (isDraft = false) => {
        if (!isDraft && !validateForm()) {
            return;
        }

        setLoading(true);
        try {
            const payload = {
                id: formData.id,
                code: formData.code,
                name: formData.name.trim(),
                description: formData.description.trim(),
                content: formData.content,
                is_active: formData.is_active,
                company: formData.company_id,
                is_draft: isDraft
            };

            let response;
            if (fromEdit) {
                response = await update(LETTER_TEMPLATE_API, payload);
            } else {
                response = await create(LETTER_TEMPLATE_API, payload);
            }

            if (response?.status === "success") {
                ToastMsg("success", response.message || `Template ${fromEdit ? 'updated' : 'created'} successfully`);
                handleBack();
            } else {
                ToastMsg("error", response?.message || `Failed to ${fromEdit ? 'update' : 'create'} template`);
            }
        } catch (error) {
            console.error('Error saving template:', error);
            ToastMsg("error", "An error occurred while saving the template");
        } finally {
            setLoading(false);
        }
    };

    const handleSaveAsDraft = useCallback(() => {
        handleSave(true);
    }, [formData]);

    const handleCancel = () => {
        handleBack();
    };

    // Insert variable into editor at cursor position
    const insertVariable = useCallback((variable) => {
        if (editorRef.current) {
            editorRef.current.insertText(variable);
        }
    }, []);

    // Filter variables based on search
    const filteredVariables = useMemo(() =>
        letterVariables.filter((v) =>
            v.label.toLowerCase().includes(searchTerm.toLowerCase())
        ),
        [letterVariables, searchTerm]
    );

    // Check for edited variables
    const hasEditedVariable = useCallback((content) => {
        if (!content) return false;

        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = content;
        const textContent = tempDiv.textContent || tempDiv.innerText || '';

        const usedVariables = textContent.match(/{(.*?)}/g) || [];
        const validVariableTags = letterVariables.map((v) => `{${v.key}}`);

        for (let variable of usedVariables) {
            if (!validVariableTags.includes(variable)) {
                return true;
            }
        }

        return false;
    }, [letterVariables]);

    // Check if form has data for draft button
    const hasFormData = Object.values(formData).some(value =>
        value !== "" && value !== undefined && value !== null && value !== false
    );

    return (
        <ContainerCard>
            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
                <ChevronLeftIcon className="h-5 w-5 cursor-pointer" onClick={handleBack} />
                <div className="flex-1">
                    <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {fromEdit ? "Edit Letter Template" : "Add Letter Template"}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Create and manage letter templates with dynamic variables
                    </p>
                </div>

                {/* Active Status Toggle */}
                <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                    <div className="flex items-center gap-3">
                        <span className={cn(
                            "text-sm font-medium",
                            formData.is_active
                                ? "text-green-600 dark:text-green-400"
                                : "text-gray-500 dark:text-gray-400"
                        )}>
                            {formData.is_active ? "Active" : "Inactive"}
                        </span>
                        <button
                            type="button"
                            onClick={() => handleInputChange('is_active', !formData.is_active)}
                            className={cn(
                                "relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200",
                                formData.is_active ? "bg-sidebar-primary" : "bg-gray-300 dark:bg-gray-600",
                                fromEdit && !isActive ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                            )}
                            disabled={fromEdit && !isActive}
                        >
                            <span
                                className={cn(
                                    "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-200",
                                    formData.is_active ? "translate-x-4" : "translate-x-1"
                                )}
                            />
                        </button>
                    </div>
                </div>
            </div>

            {/* Form Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                    {/* Template Code - Only show in edit mode */}
                    {fromEdit && (
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Template Code <span className="text-red-500">*</span>
                            </label>
                            <Input
                                value={formData.code}
                                onChange={(e) => handleInputChange('code', e.target.value)}
                                placeholder="Enter template code"
                                className={cn(
                                    "w-full rounded-md transition-colors duration-200",
                                    "border-gray-300 dark:border-gray-600",
                                    "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                    "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                    "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                                )}
                                disabled={fromEdit}
                                maxLength={10}
                            />
                        </div>
                    )}

                    {/* Template Name */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Template Name <span className="text-red-500">*</span>
                        </label>
                        <Input
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            placeholder="Enter template name"
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                            maxLength={50}
                        />
                    </div>

                    {/* Template Description */}
                    <div className="lg:col-span-2 space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Template Description <span className="text-red-500">*</span>
                        </label>
                        <Input
                            value={formData.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            placeholder="Enter template description"
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                            maxLength={150}
                        />
                    </div>
                </div>

                {/* Variables Section */}
                <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <div className="flex items-center gap-2 mb-3">
                        <Variable className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300">
                            Available Variables
                        </h3>
                    </div>

                    {/* Search for variables */}
                    {letterVariables.length > 5 && (
                        <div className="mb-3">
                            <Input
                                placeholder="Search variables..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="max-w-xs"
                                size="sm"
                            />
                        </div>
                    )}

                    {/* Variables List */}
                    <div className="flex flex-wrap gap-2">
                        {filteredVariables.map((variable) => (
                            <button
                                key={variable.key}
                                type="button"
                                onClick={() => insertVariable(`{${variable.key}}`)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-white dark:bg-gray-700 border border-blue-200 dark:border-blue-700 rounded-full text-xs font-medium text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors duration-200 shadow-sm"
                                title={`Insert ${variable.label}`}
                            >
                                <Variable className="w-3 h-3" />
                                {variable.label}
                            </button>
                        ))}
                    </div>

                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-3">
                        Click on any variable to insert it into the template content
                    </p>
                </div>

                {/* Content Editor */}
                <div className="space-y-2 mb-6">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Template Content <span className="text-red-500">*</span>
                    </label>

                    <CustomTextEditor
                        ref={editorRef}
                        value={formData.content}
                        onChange={(content) => {
                            handleInputChange('content', content);
                            if (hasEditedVariable(content)) {
                                ToastMsg("warning", "You have edited a variable! This may affect the template functionality.");
                            }
                        }}
                        className="font-sans"
                        placeholder="Write your letter template content here..."
                    />

                    <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                        <div className="flex items-start gap-2">
                            <div className="flex-shrink-0">
                                <div className="w-5 h-5 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center">
                                    <svg className="w-3 h-3 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                </div>
                            </div>
                            <div className="flex-1">
                                <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-2">
                                    Editor Tips
                                </h4>
                                <div className="text-xs text-blue-700 dark:text-blue-400 space-y-1.5">
                                    <div className="flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 bg-blue-400 dark:bg-blue-500 rounded-full"></div>
                                        <span><strong>Select text first</strong> before applying formatting</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 bg-blue-400 dark:bg-blue-500 rounded-full"></div>
                                        <span>Use keyboard shortcuts:
                                            <kbd className="ml-1 px-1.5 py-0.5 text-xs font-mono bg-white dark:bg-gray-700 border border-blue-300 dark:border-blue-600 rounded shadow-sm">Ctrl+B</kbd>
                                            <kbd className="ml-1 px-1.5 py-0.5 text-xs font-mono bg-white dark:bg-gray-700 border border-blue-300 dark:border-blue-600 rounded shadow-sm">Ctrl+I</kbd>
                                            <kbd className="ml-1 px-1.5 py-0.5 text-xs font-mono bg-white dark:bg-gray-700 border border-blue-300 dark:border-blue-600 rounded shadow-sm">Ctrl+U</kbd>
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 bg-blue-400 dark:bg-blue-500 rounded-full"></div>
                                        <span>Insert variables using the buttons above</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 bg-blue-400 dark:bg-blue-500 rounded-full"></div>
                                        <span>Use alignment buttons for paragraph formatting</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Required Fields Note */}
                <div className="mb-6 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <p className="text-xs text-blue-700 dark:text-blue-300">
                        <span className="text-red-500">*</span> indicates required fields
                    </p>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <Button
                        variant="outline"
                        onClick={handleCancel}
                        disabled={loading}
                        className="flex items-center gap-2"
                    >
                        <X className="w-4 h-4" />
                        Cancel
                    </Button>

                    {/* Draft Button - Only show in create mode */}
                    {!fromEdit && (
                        <Button
                            onClick={handleSaveAsDraft}
                            disabled={loading || !hasFormData}
                            className={cn(
                                "bg-gray-600 hover:bg-gray-700",
                                "text-white transition-colors duration-200",
                                "flex items-center gap-2",
                                (!hasFormData) && "opacity-50 cursor-not-allowed"
                            )}
                        >
                            Save as Draft
                        </Button>
                    )}

                    <Button
                        onClick={() => handleSave(false)}
                        disabled={loading}
                        className={cn(
                            "bg-sidebar hover:bg-sidebar-primary",
                            "dark:bg-blue-600 dark:hover:bg-blue-700",
                            "text-white transition-colors duration-200",
                            "flex items-center gap-2"
                        )}
                    >
                        {loading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        ) : (
                            <Save className="w-4 h-4" />
                        )}
                        {loading ? "Saving..." : (fromEdit ? "Update Template" : "Save Template")}
                    </Button>
                </div>
            </div>
        </ContainerCard>
    );
};

export default LetterTemplateAddEdit;